import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { fetchAllRows } from "@/lib/supabase-fetch-all";
import { toast } from "sonner";

// Types - Global config (no longer clinic-specific)
export interface SmilePointsConfigGlobal {
  id: string;
  earning_rate_per_100: number;
  referral_bonus_points: number;
  birthday_bonus_points: number;
  review_bonus_points: number;
  redemption_value_per_point: number;
  min_points_to_redeem: number;
  max_redemption_percentage: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Legacy interface kept for backwards compatibility
export interface SmilePointsConfig {
  id: string;
  clinic_id: string;
  earning_rate_per_100: number;
  referral_bonus_points: number;
  birthday_bonus_points: number;
  redemption_value_per_point: number;
  min_points_to_redeem: number;
  max_redemption_percentage: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Global patient points (not clinic-specific)
export interface PatientSmilePointsGlobal {
  id: string;
  patient_id: string;
  balance: number;
  lifetime_earned: number;
  lifetime_redeemed: number;
  created_at: string;
  updated_at: string;
}

// Legacy interface kept for backwards compatibility
export interface PatientSmilePoints {
  id: string;
  patient_id: string;
  clinic_id: string;
  balance: number;
  lifetime_earned: number;
  lifetime_redeemed: number;
  created_at: string;
  updated_at: string;
}

export interface SmilePointsTransaction {
  id: string;
  patient_points_id: string;
  global_points_id?: string;
  clinic_id?: string;
  transaction_type: 'earned_payment' | 'earned_referral' | 'earned_birthday' | 'earned_review' | 'redeemed' | 'manual_credit' | 'manual_debit' | 'expired';
  points: number;
  reference_type?: string;
  reference_id?: string;
  description?: string;
  created_by?: string;
  created_at: string;
  created_by_profile?: {
    full_name: string;
  };
  clinic?: {
    name: string;
    code: string;
  };
}

// Hook to get GLOBAL smile points config (no longer clinic-specific)
export const useSmilePointsConfig = () => {
  return useQuery({
    queryKey: ["smile-points-config-global"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("smile_points_config_global")
        .select("*")
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as SmilePointsConfigGlobal | null;
    },
  });
};

// Hook to update global smile points config
export const useUpdateSmilePointsConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: Partial<SmilePointsConfigGlobal>) => {
      // First check if config exists
      const { data: existing } = await supabase
        .from("smile_points_config_global")
        .select("id")
        .limit(1)
        .maybeSingle();

      let result;
      if (existing) {
        // Update existing
        const { data, error } = await supabase
          .from("smile_points_config_global")
          .update(config)
          .eq("id", existing.id)
          .select()
          .single();
        if (error) throw error;
        result = data;
      } else {
        // Insert new
        const { data, error } = await supabase
          .from("smile_points_config_global")
          .insert(config)
          .select()
          .single();
        if (error) throw error;
        result = data;
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["smile-points-config-global"] });
      toast.success("Pure Smile Rewards configuration saved");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to save configuration");
    },
  });
};

// Hook to get patient's GLOBAL smile points balance (not clinic-specific)
export const usePatientSmilePoints = (patientId?: string) => {
  return useQuery({
    queryKey: ["patient-smile-points-global", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("patient_smile_points_global")
        .select("*")
        .eq("patient_id", patientId!)
        .maybeSingle();

      if (error) throw error;
      return data as PatientSmilePointsGlobal | null;
    },
  });
};

// Hook to get smile points transactions for a patient (using global_points_id)
export const useSmilePointsTransactions = (globalPointsId?: string) => {
  return useQuery({
    queryKey: ["smile-points-transactions", globalPointsId],
    enabled: !!globalPointsId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("smile_points_transactions")
        .select(`
          *,
          created_by_profile:profiles!smile_points_transactions_created_by_fkey(full_name),
          clinic:clinics(name, code)
        `)
        .eq("global_points_id", globalPointsId!)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as SmilePointsTransaction[];
    },
  });
};

// Hook to award points (now global, but tracks which clinic awarded them)
export const useAwardPoints = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      patientId,
      clinicId,
      points,
      transactionType,
      referenceType,
      referenceId,
      description,
    }: {
      patientId: string;
      clinicId: string;
      points: number;
      transactionType: SmilePointsTransaction['transaction_type'];
      referenceType?: string;
      referenceId?: string;
      description?: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) throw new Error("You must be logged in");

      // Get or create GLOBAL patient points record using RPC
      const { data: globalPointsId, error: rpcError } = await supabase
        .rpc("get_or_create_global_smile_points", {
          p_patient_id: patientId,
        });

      if (rpcError) throw rpcError;

      // Also get/create legacy record for backwards compatibility
      const { data: legacyPointsId } = await supabase
        .rpc("get_or_create_smile_points", {
          p_patient_id: patientId,
          p_clinic_id: clinicId,
        });

      // Create transaction with both global and legacy references
      const { data, error } = await supabase
        .from("smile_points_transactions")
        .insert({
          patient_points_id: legacyPointsId,
          global_points_id: globalPointsId,
          clinic_id: clinicId,
          transaction_type: transactionType,
          points: points,
          reference_type: referenceType,
          reference_id: referenceId,
          description: description,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["patient-smile-points-global", variables.patientId] });
      queryClient.invalidateQueries({ queryKey: ["all-patients-smile-points-global"] });
      queryClient.invalidateQueries({ queryKey: ["smile-points-transactions"] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to award points");
    },
  });
};

// Hook to redeem points (now global)
export const useRedeemPoints = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      patientId,
      clinicId,
      points,
      billId,
      description,
    }: {
      patientId: string;
      clinicId: string;
      points: number;
      billId: string;
      description?: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) throw new Error("You must be logged in");

      // Get global config for validation
      const { data: config, error: configError } = await supabase
        .from("smile_points_config_global")
        .select("min_points_to_redeem, is_active")
        .limit(1)
        .maybeSingle();

      if (configError) throw configError;

      // Validate program is active
      if (!config?.is_active) {
        throw new Error("PureSmile Rewards program is currently inactive");
      }

      // Validate minimum points threshold
      if (config?.min_points_to_redeem && points < config.min_points_to_redeem) {
        throw new Error(`Minimum ${config.min_points_to_redeem} points required to redeem`);
      }

      // Get patient's GLOBAL points record
      const { data: patientPoints, error: pointsError } = await supabase
        .from("patient_smile_points_global")
        .select("id, balance")
        .eq("patient_id", patientId)
        .single();

      if (pointsError) throw pointsError;
      if (!patientPoints) throw new Error("No points record found");
      
      // Validate sufficient balance before redemption to prevent over-redemption
      if (patientPoints.balance < points) {
        throw new Error(`Insufficient points balance. Available: ${patientPoints.balance}, Requested: ${points}`);
      }

      // Get legacy points ID for backwards compatibility
      const { data: legacyPointsId } = await supabase
        .rpc("get_or_create_smile_points", {
          p_patient_id: patientId,
          p_clinic_id: clinicId,
        });

      // Create redemption transaction (negative points)
      const { data, error } = await supabase
        .from("smile_points_transactions")
        .insert({
          patient_points_id: legacyPointsId,
          global_points_id: patientPoints.id,
          clinic_id: clinicId,
          transaction_type: "redeemed",
          points: -points,
          reference_type: "bill",
          reference_id: billId,
          description: description || `Redeemed ${points} points on bill`,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) throw error;

      // Log in bill audit — audit failures must never be silent (this action
      // was constraint-rejected for months unnoticed).
      const { error: auditError } = await supabase.from("bill_audit_log").insert({
        bill_id: billId,
        action: "points_redeemed",
        changed_by: user.id,
        new_values: {
          points_redeemed: points,
        },
      });
      if (auditError) throw auditError;

      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["patient-smile-points-global", variables.patientId] });
      queryClient.invalidateQueries({ queryKey: ["all-patients-smile-points-global"] });
      queryClient.invalidateQueries({ queryKey: ["smile-points-transactions"] });
      queryClient.invalidateQueries({ queryKey: ["bill", variables.billId] });
      toast.success(`Successfully redeemed ${variables.points} points`);
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to redeem points");
    },
  });
};

// Hook to get all patients with smile points GLOBALLY (for dashboard)
// Shows all patients with any points history (including zero/negative balances)
export const useAllPatientsSmilePoints = () => {
  return useQuery({
    queryKey: ["all-patients-smile-points-global"],
    queryFn: async () => {
      // Page past PostgREST max-rows (1000) — unbounded fetch truncated silently
      const data = await fetchAllRows<any>(
        (from, to) =>
          supabase
            .from("patient_smile_points_global")
            .select(`
              *,
              patient:patients(id, name, phone, registration_number, clinic_id)
            `)
            .or("balance.neq.0,lifetime_earned.gt.0,lifetime_redeemed.gt.0")
            .order("balance", { ascending: false })
            .order("id", { ascending: true })
            .range(from, to),
        { label: "useAllPatientsSmilePoints" }
      );
      return data;
    },
  });
};
